import { useIsFocused } from '@react-navigation/native';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getPodcastUrlFromId } from '../api/mobileApi';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { DataState } from '../constants';
import { selectBearerToken } from '../features/bearertoken/store/selectors';
import {
  fetchNextPodcastFeedPage,
  fetchPodcastFeed,
  togglePodcastFeedOrder,
} from '../features/podcast/store/actions';
import {
  selectPodcastFeedCurrentPage,
  selectPodcastFeedData,
  selectPodcastFeedDataState,
  selectPodcastFeedLastPage,
  selectPodcastFeedOrder,
} from '../features/podcast/store/selectors';
import { PodcastModel } from '../models/Podcast.model';

interface UsePodcastFeedDataProps {
  podcastUrl: string;
  tabName?: string;
  podcastId?: string;
}

interface UsePodcastFeedDataReturn {
  podcastFeedsContent: PodcastModel | null;
  podcastFeedsContentState: DataState;
  currentPage: number;
  lastPage: number;
  order: 'desc' | 'asc';
  toggleOrder: () => void;
  refetch: () => Promise<void>;
  loadNextPage: () => Promise<void>;
}

export const usePodcastFeedData = ({
  podcastUrl,
  tabName,
  podcastId,
}: UsePodcastFeedDataProps): UsePodcastFeedDataReturn => {
  const dispatch = useAppDispatch();
  const isFocused = useIsFocused();
  const bearerToken = useAppSelector(selectBearerToken);

  // keep track of the last requested page to avoid duplicate dispatches
  const lastRequestedPageRef = useRef<number | null>(null);
  const [podcastUrlState, setPodcastUrlState] = useState<string>(podcastUrl);

  const podcastFeedsContent = useAppSelector(selectPodcastFeedData(podcastUrlState));
  const podcastFeedsContentState = useAppSelector(
    selectPodcastFeedDataState(podcastUrlState),
  );
  const currentPage = useAppSelector(selectPodcastFeedCurrentPage(podcastUrlState));
  const lastPage = useAppSelector(selectPodcastFeedLastPage(podcastUrlState));
  const order = useAppSelector(selectPodcastFeedOrder(podcastUrlState));

  const fetchPodcastEpisodes = useCallback(async () => {
    if (!podcastUrl && tabName && podcastId) {
      getPodcastUrlFromId(tabName, podcastId).then(result => {
        setPodcastUrlState(result);
        dispatch(fetchPodcastFeed(result, bearerToken, order));
      });
    } else {
      setPodcastUrlState(podcastUrl);
      dispatch(fetchPodcastFeed(podcastUrl, bearerToken, order));
    }
  }, [dispatch, podcastUrl, bearerToken, tabName, podcastId, order]);

  const toggleOrder = useCallback(() => {
    dispatch(togglePodcastFeedOrder(podcastUrlState));
  }, [dispatch, podcastUrlState]);

  const loadNextPage = useCallback(async () => {
    if (currentPage < lastPage) {
      // prevent triggering twice for the same page
      if (lastRequestedPageRef.current === currentPage) {
        return;
      }
      lastRequestedPageRef.current = currentPage;
      dispatch(
        fetchNextPodcastFeedPage(podcastUrlState, currentPage, bearerToken, order),
      );
    }
  }, [dispatch, podcastUrlState, currentPage, lastPage, bearerToken, order]);

  // reset the guard when page or feed changes
  useEffect(() => {
    // allow next request when page advanced or feed changed
    lastRequestedPageRef.current = null;
  }, [currentPage, podcastUrlState]);

  useEffect(() => {
    // prevent multiple api calls when screen instances exist in navigation stack
    if (!isFocused || (!podcastUrl && !tabName && !podcastId)) {
      return;
    }

    // only fetch if we don't have data yet
    if (podcastFeedsContentState === DataState.NOT_LOADED) {
      fetchPodcastEpisodes();
    }
  }, [
    isFocused,
    podcastUrl,
    podcastFeedsContentState,
    fetchPodcastEpisodes,
    tabName,
    podcastId,
  ]);

  return {
    podcastFeedsContent,
    podcastFeedsContentState,
    currentPage,
    lastPage,
    order,
    toggleOrder,
    refetch: fetchPodcastEpisodes,
    loadNextPage,
  };
};
